import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Tabs,
    Tab,
    Button,
    CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tooltip } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { API_URL } from '../../api';
import AccountStatusChips from '../../components/AccountStatusChips';
import HowItWorksCard from '../../components/HowItWorksCard';
import { AccountLifecycleHelp } from '../../components/AccountLifecycleHelp';

const cookies = new Cookies();

const ACCOUNT_STATUS_OPTIONS = [
    { value: '', label: 'Any account status' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
];
const ENGAGEMENT_STATUS_OPTIONS = [
    { value: '', label: 'Any engagement' },
    { value: 'enrolled', label: 'Teaching' },
    { value: 'not_enrolled', label: 'Not teaching' },
    { value: 'on_break', label: 'On break' },
];

export default function TeachersAccountsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [accountStatus, setAccountStatus] = useState('');
    const [engagementStatus, setEngagementStatus] = useState('');
    const [tab, setTab] = useState('active');

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('role', 'teacher');
            params.set('page', page + 1);
            params.set('limit', rowsPerPage);
            if (q.trim()) params.set('q', q.trim());
            if (tab === 'archived') {
                params.set('accountStatus', 'archived');
            } else {
                if (accountStatus) params.set('accountStatus', accountStatus);
                else params.set('excludeArchived', '1');
            }
            if (engagementStatus) params.set('engagementStatus', engagementStatus);
            const { data } = await axios.get(`${API_URL}/api/admin/accounts?${params.toString()}`, {
                headers: { Authorization: cookies.get('TOKEN') },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [page, rowsPerPage, tab]);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    return (
        <Container>
            <Typography variant="h4">Teacher accounts</Typography>
            <HowItWorksCard>
                <Typography variant="caption" component="div" color="text.secondary">
                    <strong>Columns:</strong><br />
                    <strong>Can teach</strong> — subjects + levels the tutor self-declared at
                    signup. Editable by the tutor on their portal profile. Used to shortlist
                    who fits a new class; <em>not</em> the same as current class assignments.<br />
                    <strong>Current classes</strong> — the live (<em>published</em>) tutoring
                    classes admin has set this tutor as the tutor of, with student count. Each
                    row links to the class detail page.<br />
                    <strong>Verified / Last login</strong> — email verification state + last
                    successful portal login.<br />
                    <br />
                </Typography>
                <AccountLifecycleHelp roleLabel="teacher" />
            </HowItWorksCard>
            <Box sx={{ mb: 3 }} />

            <Tabs
                value={tab}
                onChange={(_, v) => {
                    setTab(v);
                    if (page !== 0) setPage(0);
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="active" label="Active" />
                <Tab value="archived" label="Archived" />
            </Tabs>

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Search name / email"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ flex: 1, maxWidth: 320 }}
                    />
                    {tab === 'active' && (
                        <TextField
                            select
                            size="small"
                            label="Account status"
                            value={accountStatus}
                            onChange={(e) => setAccountStatus(e.target.value)}
                            sx={{ minWidth: 180 }}
                        >
                            {ACCOUNT_STATUS_OPTIONS.map((o) => (
                                <MenuItem key={o.value || 'any'} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        select
                        size="small"
                        label="Engagement"
                        value={engagementStatus}
                        onChange={(e) => setEngagementStatus(e.target.value)}
                        sx={{ minWidth: 170 }}
                    >
                        {ENGAGEMENT_STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'any'} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button type="submit" variant="contained">
                        Search
                    </Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Teacher</TableCell>
                                    <Tooltip title="Subjects + levels the tutor said they can teach when they signed up. Not the same as current class assignments.">
                                        <TableCell>Can teach</TableCell>
                                    </Tooltip>
                                    <Tooltip title="Live classes admin has assigned this tutor to, with student count.">
                                        <TableCell>Current classes</TableCell>
                                    </Tooltip>
                                    <TableCell>Last login</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((t) => (
                                    <TableRow key={t._id} hover>
                                        <TableCell sx={{ minWidth: 200 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {t.firstName} {t.surname}
                                                <Tooltip title={t.emailVerifiedAt ? 'Email verified' : 'Email not verified'}>
                                                    {t.emailVerifiedAt
                                                        ? <CheckCircleRoundedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                                        : <ErrorOutlineRoundedIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
                                                </Tooltip>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                {t.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 180, maxWidth: 260 }}>
                                            <TeachingSubjectsCell subjects={t.teachingSubjects} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 160, maxWidth: 240 }}>
                                            {t.currentClasses?.length ? (
                                                <Stack spacing={0.25}>
                                                    {t.currentClasses.map((c) => (
                                                        <Link
                                                            key={c._id}
                                                            component={RouterLink}
                                                            to={`/tutoring-classes/${c._id}`}
                                                            underline="hover"
                                                            sx={{
                                                                fontSize: 13,
                                                                color: 'text.primary',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {c.name} · {c.studentCount} student{c.studentCount === 1 ? '' : 's'}
                                                        </Link>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    None
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t.lastLoginAt
                                                    ? new Date(t.lastLoginAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                                    : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <AccountStatusChips account={t} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button component={RouterLink} to={`/teacher-accounts/${t._id}`} size="small">
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No teacher accounts.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
        </Container>
    );
}

/**
 * Compact "Can teach" cell. Shows the first subject inline; when the
 * tutor has more than one subject, a "+N more" pill expands the rest
 * without leaving the row.
 */
function TeachingSubjectsCell({ subjects }) {
    const [expanded, setExpanded] = useState(false);
    const items = useMemo(
        () =>
            (subjects || []).map((s) => ({
                subject: s.subject,
                levels: (s.levels || []).join(', '),
            })),
        [subjects]
    );

    if (!items.length) return <Typography variant="body2" color="text.secondary">—</Typography>;

    const rendered = expanded ? items : items.slice(0, 1);
    const extra = items.length - rendered.length;

    return (
        <Box>
            <Stack spacing={0.25}>
                {rendered.map((s, i) => (
                    <Typography
                        key={`${s.subject}-${i}`}
                        variant="body2"
                        sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {s.subject}{s.levels ? ` · ${s.levels}` : ''}
                    </Typography>
                ))}
            </Stack>
            {extra > 0 && (
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => setExpanded(true)}
                    sx={{ fontSize: 12, mt: 0.5, color: 'primary.main' }}
                >
                    +{extra} more
                </Link>
            )}
            {expanded && items.length > 1 && (
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => setExpanded(false)}
                    sx={{ fontSize: 12, mt: 0.5, color: 'text.secondary' }}
                >
                    Show less
                </Link>
            )}
        </Box>
    );
}
